import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { IntlShape, useIntl } from 'react-intl';
import * as z from 'zod';

import { T } from '@/components/i18n/message.i18n.component';
import Button from '@/components/ui/button/button.component';
import { Checkbox } from '@/components/ui/checkbox/checkbox.component';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form/form.component';
import { Textarea } from '@/components/ui/textarea/textarea.component';
import {
  Inclusion,
  ManualBuildingBlockInclusion,
  ProductBuildingBlock,
  ReevaluationRequest,
  TailorBuildingBlockInput,
} from '@/gql/graphql';
import { useToast } from '@/hooks/use-toast.hook';
import { useDialogStore } from '@/store/dialog.store';
import { useMutation } from '@apollo/client';
import { formValidationMessages, toastTranslations } from '../../all/products.messages';
import { tailorBuildingBlock } from '../../products.requests';
import { tailorFormsTranslations } from '../tailoring-forms.messages';

function formSchema(t: IntlShape) {
  return z.object({
    inclusionExplanation: z
      .string()
      .transform((value) => value.trim())
      .pipe(
        z
          .string()
          .min(1, {
            message: t.formatMessage(formValidationMessages.minimumLength, {
              minLength: 1,
            }),
          })
          .max(60, {
            message: t.formatMessage(formValidationMessages.maximumLength, {
              maxLength: 60,
            }),
          }),
      ),
    reevaluationRequest: z.boolean(),
  });
}

type TailorBuildingBlockFormProps = {
  bb: ProductBuildingBlock;
  productId?: string;
  afterAdd?: (payload: TailorBuildingBlockInput) => void;
};

const includedBB = (bb: ProductBuildingBlock) =>
  bb.inclusion.actual === Inclusion.Included || bb.inclusion.actual === Inclusion.Pending;

export const TailorBuildingBlockForm = ({ afterAdd, bb, productId }: TailorBuildingBlockFormProps) => {
  const intl = useIntl();
  const { toast } = useToast();

  const [tailorBuildingBlockMutation, { loading }] = useMutation(tailorBuildingBlock);

  const { handleDialogClose } = useDialogStore((state) => ({
    handleDialogClose: state.closeDialog,
  }));

  const form = useForm<z.infer<ReturnType<typeof formSchema>>>({
    resolver: zodResolver(formSchema(intl)),
    defaultValues: {
      inclusionExplanation: '',
      reevaluationRequest: false,
    },
  });

  async function onSubmit(values: z.infer<ReturnType<typeof formSchema>>) {
    if (!productId) return;

    const payload = {
      buildingBlockId: bb.id,
      productId,
      included: !includedBB(bb) ? ManualBuildingBlockInclusion.Included : ManualBuildingBlockInclusion.Excluded,
      inclusionExplanation: values.inclusionExplanation,
      reevaluationRequest: values.reevaluationRequest ? ReevaluationRequest.Requested : ReevaluationRequest.NotRequired,
    };

    await tailorBuildingBlockMutation({
      variables: {
        tailorBuildingBlockInput: payload,
      },
      refetchQueries: ['getProduct'],
    });
    handleDialogClose();
    toast({
      variant: 'default',
      description: intl.formatMessage(
        includedBB(bb) ? toastTranslations.excludeBuildingBlockToast : toastTranslations.includeBuildingBlockToast,
      ),
    });
    if (afterAdd) afterAdd(payload);
  }

  return (
    <Form {...form} data-testid="bb-tailoring-form-container">
      <form
        data-testid="tailor-bb-form"
        onSubmit={form.handleSubmit(onSubmit)}
        className="space-y-4 active:border-none"
      >
        <FormField
          control={form.control}
          name="inclusionExplanation"
          // eslint-disable-next-line react/jsx-handler-names
          render={({ field }) => (
            <FormItem>
              <div className="flex flex-row justify-between items-center mb-3">
                <FormLabel data-testid="bb-tailoring-form-label-explanation" required>
                  {includedBB(bb) && <T {...tailorFormsTranslations.bbTailoringFormExclusionExplanationLabel} />}
                  {!includedBB(bb) && <T {...tailorFormsTranslations.bbTailoringFormInclusionExplanationLabel} />}
                </FormLabel>
                <FormMessage data-testid="bb-tailoring-form-error-explanation" />
              </div>
              <FormControl>
                <Textarea
                  data-testid="bb-tailoring-form-field-explanation"
                  placeholder={intl.formatMessage(
                    includedBB(bb)
                      ? tailorFormsTranslations.bbTailoringFormExclusionExplanationPlaceholder
                      : tailorFormsTranslations.bbTailoringFormInclusionExplanationPlaceholder,
                  )}
                  {...field}
                />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="reevaluationRequest"
          // eslint-disable-next-line react/jsx-handler-names
          render={({ field }) => (
            <FormItem>
              <FormMessage />
              <div className="flex flex-row justify-start gap-x-2 items-center mb-3">
                <FormControl>
                  <Checkbox
                    data-testid="bb-tailoring-form-field-reevaluation"
                    checked={field.value}
                    // eslint-disable-next-line react/jsx-handler-names
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <FormLabel data-testid="bb-tailoring-form-label-reevaluation" className="cursor-pointer">
                  <T {...tailorFormsTranslations.bbTailoringFormReevaluationLabel} />
                </FormLabel>
              </div>
            </FormItem>
          )}
        />
        <div className="flex flex-row justify-between">
          <Button
            data-testid="bb-tailoring-form-button-cancel"
            onClick={handleDialogClose}
            type="button"
            variant="secondary"
          >
            <T {...tailorFormsTranslations.bbTailoringFormCancelButton} />{' '}
          </Button>
          <Button
            loading={loading}
            data-testid="bb-tailoring-form-button-add"
            disabled={!form.formState.isDirty}
            type="submit"
            variant="default"
          >
            {includedBB(bb) && <T {...tailorFormsTranslations.bbTailoringFormExclusionSubmitButton} />}
            {!includedBB(bb) && <T {...tailorFormsTranslations.bbTailoringFormInclusionSubmitButton} />}
          </Button>
        </div>
      </form>
    </Form>
  );
};
